import { isElectron, isNative, openExternalUrl } from '@/app/nativeBridge';
import { HAS_EVER_LOGGED_IN_KEY, type AuthClient, type User } from '@/auth/auth';
import { waitForAuthClientToRedirect } from '@/auth/auth.helper';
import { VITE_AUTH_TYPE, VITE_WORKOS_CLIENT_ID } from '@/env-vars';
import { ROUTES } from '@/shared/constants/routes';
import { captureEvent } from '@sentry/react';
import { createClient } from '@workos-inc/authkit-js';
import { redirect } from 'react-router';

// Deep-link scheme registered by the native shell (Electron via
// `app.setAsDefaultProtocolClient('quadratic')`). The WorkOS authorize
// URL opens in the system browser, and after sign-in WorkOS redirects
// to this URL which the OS hands off to the running app.
//
// The URL MUST contain `login-result` in the path (not as the hostname).
// authkit-js's `isRedirectCallback` check compares `new URL(redirectUri).pathname`
// to `window.location.pathname` — and for `quadratic://login-result` the URL
// parser treats `login-result` as the host, giving an empty pathname, which
// never matches the WebView's `/login-result`. Using
// `quadratic://auth-callback/login-result` produces pathname `/login-result`,
// so the callback is correctly detected.
//
// NOTE: The WorkOS dashboard must list this exact URL in the project's
// allowed redirect URIs, and `toInAppPath` in `nativeDeepLinks.ts` must
// recognize it.
const NATIVE_REDIRECT_URI = 'quadratic://auth-callback/login-result';

// verify all Workos env variables are set (only when Workos auth is enabled)
if (VITE_AUTH_TYPE === 'workos' && !VITE_WORKOS_CLIENT_ID) {
  const message = 'Workos variables are not configured correctly.';
  captureEvent({
    message,
    level: 'fatal',
  });
  throw new Error(message);
}

// Helper function to extract the last two parts of a hostname
// e.g., "workos-attempt-3.quadratic-preview.com" -> "quadratic-preview.com"
function getBaseDomain(hostname: string): string {
  const parts = hostname.split('.');
  if (parts.length <= 2) {
    return hostname;
  }
  return parts.slice(-2).join('.');
}

type WorkOSClient = Awaited<ReturnType<typeof createClient>>;

let client: WorkOSClient | null = null;

// Shared promise prevents concurrent initialization (e.g., parallel route loaders)
let clientPromise: Promise<WorkOSClient> | null = null;

// Store the state from the redirect callback
let redirectState: Record<string, any> | null = null;

// Shared promise prevents concurrent sign-in attempts. Every AuthKit
// `signIn`/`signUp`/`getSignInUrl`/`getSignUpUrl` call generates a fresh PKCE
// `code_verifier` and overwrites the previous one in `sessionStorage`, but the
// browser only ever follows one of the authorize URLs. If several callers start
// a login at once (e.g. parallel API requests in `fetchFromApi` that all
// discover the session expired), the `code` WorkOS sends back to
// /login-result belongs to an earlier challenge than the stored verifier, and
// the exchange fails with "Invalid client secret."
let loginPromise: Promise<void> | null = null;

// Matches browser-level fetch failures (e.g., DNS, connectivity)
function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError && (e.message.includes('Failed to fetch') || e.message.includes('NetworkError'));
}

export function isExpiredWorkosAuthCodeError(e: unknown): boolean {
  return e instanceof Error && /^The code '[^']+' has expired\.$/.test(e.message);
}

// Creates and initializes the WorkOS client. createClient() calls
// client.initialize() internally, which exchanges the OAuth code
// if the current URL is a redirect callback.
async function createWorkosClient(): Promise<WorkOSClient> {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  // The native shell uses the `quadratic://` deep-link scheme so the OS
  // can hand the OAuth callback back to the app; the WebView's own
  // origin (`app://localhost` for Electron) is not a valid OAuth
  // redirect target.
  const redirectUri = isNative() ? NATIVE_REDIRECT_URI : window.location.origin + ROUTES.LOGIN_RESULT;
  const newClient = await createClient(VITE_WORKOS_CLIENT_ID, {
    redirectUri,
    apiHostname: isLocalhost ? undefined : `authenticate.${getBaseDomain(hostname)}`,
    https: true,
    onBeforeAutoRefresh: () => true,
    onRedirectCallback: (params) => {
      redirectState = params.state;
    },
  });
  if (!newClient) throw new Error('Failed to create WorkOS client');
  return newClient;
}

// Best-effort retry on transient network errors. In most cases the OAuth
// code is still valid because the request never reached the server, but if
// the connection dropped mid-flight the code may already be consumed and
// the retry will fail with an auth error — callers should handle that.
async function initializeClientWithRetry(): Promise<WorkOSClient> {
  try {
    return await createWorkosClient();
  } catch (firstError) {
    if (!isNetworkError(firstError)) throw firstError;
    console.warn('WorkOS client initialization failed with network error, retrying…', firstError);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      return await createWorkosClient();
    } catch (retryError) {
      if (isExpiredWorkosAuthCodeError(retryError)) {
        console.warn('WorkOS client retry failed because the OAuth code expired or was already used:', retryError);
      } else {
        console.error('WorkOS client retry also failed (original error logged above):', retryError);
      }
      throw retryError;
    }
  }
}

// Returns the singleton WorkOS client, initializing it on first call.
// Multiple concurrent callers (e.g., parallel route loaders) share
// the same promise. On failure the promise is cleared so the next
// call can retry with a fresh initialization attempt.
async function getClient(): Promise<WorkOSClient> {
  if (client) {
    return client;
  }
  if (!clientPromise) {
    clientPromise = initializeClientWithRetry();
  }
  try {
    client = await clientPromise;
    return client;
  } catch (e) {
    clientPromise = null;
    throw e;
  }
}

// Starts the OAuth flow: generates the PKCE challenge and hands the user off
// to WorkOS. Must only be called once per document — see `loginPromise`.
async function beginLogin(args: { redirectTo: string; isSignupFlow?: boolean }): Promise<void> {
  const { redirectTo, isSignupFlow } = args;
  const client = await getClient();

  // Only include state if we have a redirectTo that's not '/'
  const options: { state?: any } = {};
  if (redirectTo && redirectTo !== '/') {
    options.state = { redirectTo };
  }

  if (isNative()) {
    const url = isSignupFlow ? await client.getSignUpUrl(options) : await client.getSignInUrl(options);
    await openExternalUrl(url);
    return;
  }

  if (isSignupFlow) {
    await client.signUp(options);
  } else {
    await client.signIn(options);
  }
}

// Peek at redirect state without clearing it
function peekRedirectState(): { redirectTo?: string; closeOnComplete?: boolean } | null {
  return redirectState;
}

// Get and clear redirect state
export function getAndClearRedirectState(): { redirectTo?: string; closeOnComplete?: boolean } | null {
  const state = redirectState;
  redirectState = null; // Clear after reading
  return state;
}

export const workosClient: AuthClient = {
  /**
   * Return whether the user is authenticated and the session is valid.
   */
  async isAuthenticated(): Promise<boolean> {
    const client = await getClient();
    const user = client.getUser();
    return !!user;
  },

  /**
   * Get the current authenticated user from WorkOS AuthKit.
   */
  async user(): Promise<User | undefined> {
    const client = await getClient();
    const workosUser = client.getUser();
    if (!workosUser) {
      return undefined;
    }
    const user: User = {
      name: `${workosUser.firstName ?? ''} ${workosUser.lastName ?? ''}`.trim(),
      given_name: workosUser.firstName ?? undefined,
      family_name: workosUser.lastName ?? undefined,
      picture: workosUser.profilePictureUrl ?? undefined,
      email: workosUser.email,
      sub: workosUser.id,
    };
    return user;
  },

  /**
   * Login the user in Workos and create a new session.
   *
   * Under a native shell we cannot let AuthKit redirect the WebView to
   * WorkOS (that would navigate the app itself away), so we fetch the
   * authorize URL via `getSignInUrl` / `getSignUpUrl` and open it in the
   * system browser. AuthKit still stashes the PKCE `code_verifier` in
   * `sessionStorage`, which survives the WebView navigation triggered
   * by the `quadratic://auth-callback/login-result` deep-link callback, so the code
   * exchange on return works unmodified.
   */
  async login(args: { redirectTo: string; isSignupFlow?: boolean; href: string }): Promise<void> {
    // Only ever have one login in flight so a single PKCE `code_verifier` is
    // generated per document (see `loginPromise`).
    if (loginPromise) return loginPromise;
    loginPromise = beginLogin(args);
    try {
      await loginPromise;
      // In a browser the document is being replaced by the WorkOS authorize
      // page, so keep the guard latched for the rest of its life. Under a
      // native shell the app stays alive while the system browser handles the
      // login, so release it to let the user start over.
      if (isNative()) loginPromise = null;
    } catch (e) {
      // We never got as far as redirecting, so allow another attempt.
      loginPromise = null;
      throw e;
    }
  },

  /**
   * Handle the redirect from Workos after the user has logged in.
   * AuthKit SDK automatically handles the code exchange during initialization.
   * This function only validates the signin; the caller is responsible for redirecting.
   */
  async handleSigninRedirect(href: string): Promise<void> {
    try {
      const client = await getClient();
      if (!client.getUser()) {
        throw new Error('No user found after signin redirect');
      }

      // Handle special case: if state includes closeOnComplete, close the window
      // Use peek here so the state is preserved for login-result.tsx to use for redirect
      const state = peekRedirectState();
      if (state && 'closeOnComplete' in state && state.closeOnComplete) {
        getAndClearRedirectState(); // Clear state before closing
        window.close();
        return;
      }
    } catch (error) {
      if (isExpiredWorkosAuthCodeError(error)) {
        console.warn('WorkOS signin redirect failed because the OAuth code expired or was already used:', error);
      } else {
        console.error('WorkOS signin redirect failed:', error);
      }
      throw error;
    }
  },

  /**
   * Logout the user via AuthKit and navigate to the login page.
   */
  async logout() {
    const client = await getClient();
    // Must use navigate: false to get Promise<void> instead of void
    // This waits for server logout to complete before redirecting
    await client.signOut({ returnTo: window.location.origin, navigate: false });
    window.location.href = window.location.origin;
  },

  /**
   * Get the access token for the current authenticated user.
   * If the user is not authenticated, redirect to the login page.
   */
  async getTokenOrRedirect(skipRedirect?: boolean, request?: Request): Promise<string> {
    try {
      const client = await getClient();
      const token = await client.getAccessToken();
      window.localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');
      return token;
    } catch (e) {
      if (!skipRedirect) {
        const href = request ? request.url : window.location.href;
        const url = new URL(href);
        // Under Electron we don't auto-launch the system browser; instead
        // redirect the WebView to /login, which renders an in-app screen
        // with a button the user clicks to start the OAuth flow. See
        // ElectronLoginScreen for the UI.
        if (isElectron()) {
          throw redirect(ROUTES.LOGIN_WITH_REDIRECT(url.pathname + url.search));
        }
        const hasLoggedInBefore = !!window.localStorage.getItem(HAS_EVER_LOGGED_IN_KEY);
        await this.login({ redirectTo: url.toString(), isSignupFlow: !hasLoggedInBefore, href });
        await waitForAuthClientToRedirect();
      }
    }
    return '';
  },
};
